import { useCallback, useEffect, useRef } from 'react';

type Timer = ReturnType<typeof setTimeout>;
type SomeFunction = (...args: any[]) => void;

type DebounceOptions<Func extends SomeFunction> = {
	keySelector?: (...args: Parameters<Func>) => string;
};

export function useDebounce<Func extends SomeFunction>(
	func: Func,
	delay = 600,
	options?: DebounceOptions<Func>
) {
	const callbackRef = useRef(func);
	const delayRef = useRef(delay);
	const keySelectorRef = useRef(options?.keySelector);

	const timer = useRef<Timer | null>(null);
	const timerMap = useRef<Record<string, Timer>>({});

	// keep latest values immediately
	callbackRef.current = func;
	delayRef.current = delay;
	keySelectorRef.current = options?.keySelector;

	useEffect(() => {
		return () => {
			if (timer.current) clearTimeout(timer.current);
			Object.values(timerMap.current).forEach(clearTimeout);
		};
	}, []);

	const debouncedFunction = useCallback((...args: Parameters<Func>) => {
		const keySelector = keySelectorRef.current;
		const currentDelay = delayRef.current;

		if (keySelector) {
			const key = keySelector(...args);

			if (timerMap.current[key]) {
				clearTimeout(timerMap.current[key]);
			}

			timerMap.current[key] = setTimeout(() => {
				delete timerMap.current[key];
				callbackRef.current(...args);
			}, currentDelay);

			return;
		}

		if (timer.current) {
			clearTimeout(timer.current);
		}

		timer.current = setTimeout(() => {
			callbackRef.current(...args);
			timer.current = null;
		}, currentDelay);
	}, []) as Func;

	return debouncedFunction;
}
